import { useState } from "react";
import moment from "moment-timezone";
import { AsYouType as PhoneNumberFormatterAsYouType } from "libphonenumber-js";
import { json2csv } from "json-2-csv";

import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import { toast } from "@/components/ui/use-toast";

import { type BirthdayFundRequest, type BirthdayFundBreakdown } from "@/lib/api/fund";
import { fetchApiPost, MimeType } from "@/lib/api/utils";
import { UploadPresignedFile } from "@/lib/file";
import { downloadBinaryData } from "@/lib/browser/download";
import { CommonDestinationTimezone } from "@/lib/utils";
import { getReadableStatusLabel } from "../common";

import { ExcelComposer, type SectionConfig, type CellValue } from "@/lib/excel/composer";
import { CellFormat, numberToExcelColumn } from "@/lib/excel/common";

// ---------------------------------------------------------------------------
// Tracked-report API types (shape mirrors MarketingFeeAccrualReport)
// ---------------------------------------------------------------------------

interface CreatedTrackedReportFile {
    document_id: string;
    bucket: string;
    key: string;
    filename: string;
    put_url: string;
}

interface CreatedTrackedReport {
    tracked_report_id: string;
    report_type: string;
    source: CreatedTrackedReportFile[];
    output: CreatedTrackedReportFile[];
}

const REPORT_TYPE = "birthday_fund_granted_exported";

function buildFilenameStem(): string {
    return `birthday_fund_granted_export_${moment().format("YYYYMMDD")}`;
}

// ---------------------------------------------------------------------------
// Types matching the runtime extras seen on the requests
// ---------------------------------------------------------------------------

type ExtendedBirthdayFundRequest = BirthdayFundRequest & {
    survey: BirthdayFundRequest["survey"] & {
        data: BirthdayFundRequest["survey"]["data"] & {
            guardian_first_name?: string;
            guardian_phone_number?: string;
        };
    };
};

// ---------------------------------------------------------------------------
// Column definitions
//
// Single source of truth — used by both the CSV (source/audit file) and the
// XLSX builder so they stay aligned.
// ---------------------------------------------------------------------------

// Column order — the new "Granted Breakdown" column sits immediately after
// "Granted Amount". "Granted Date" + "Notes" are appended at the end.
const COLUMN_ORDER = [
    "nominator",
    "email",
    "guardian_first_name",
    "guardian_phone_number",
    "nominated_organization",
    "survey_nominating_organization",
    "organization",
    "birthday",
    "age",
    "location",
    "status",
    "granted",
    "projected_amount",
    "granted_amount",
    "granted_breakdown",
    "granted_date",
    "notes",
    "disbursed",
    "reconciled",
    "created_at",
] as const;

type ColumnKey = (typeof COLUMN_ORDER)[number];

const COLUMN_HEADERS: Record<ColumnKey, string> = {
    nominator: "Nominator",
    email: "Email",
    guardian_first_name: "Guardian First Name",
    guardian_phone_number: "Guardian Phone Number",
    nominated_organization: "Nominated Organization",
    survey_nominating_organization: "Survey Nominating Organization",
    organization: "Organization",
    birthday: "Birthday",
    age: "Age",
    location: "Location",
    status: "Status",
    granted: "Granted",
    projected_amount: "Projected Amount",
    granted_amount: "Granted Amount",
    granted_breakdown: "Granted Breakdown",
    disbursed: "Disbursed",
    reconciled: "Reconciled",
    created_at: "Created At",
    granted_date: "Granted Date",
    notes: "Notes",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeBreakdowns(request: BirthdayFundRequest): BirthdayFundBreakdown[] {
    // `breakdowns` field is typed singular on BirthdayFundRequest but in
    // practice can be a single object or an array (see AssociateCertificateModal
    // / BirthdayFundModal). Normalize defensively.
    const raw = request.breakdowns as unknown as
        | BirthdayFundBreakdown
        | BirthdayFundBreakdown[]
        | null
        | undefined;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (raw.id) return [raw];
    return [];
}

function formatPhone(phone: string | undefined): string {
    if (!phone) return "";
    return new PhoneNumberFormatterAsYouType("US").input(phone);
}

function formatBirthday(req: ExtendedBirthdayFundRequest): string {
    return [req.survey.data.birthday_month_of_child, req.survey.data.birthday_day_of_child]
        .filter(Boolean)
        .join(" ");
}

function parseAmount(value: string | undefined | null): number | null {
    if (value == null || value === "") return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function formatCreatedAt(value: string): string {
    return moment.tz(value, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a");
}

function formatGrantedDate(value: string | undefined): string {
    if (!value) return "";
    return moment.tz(value, CommonDestinationTimezone).format("MM/DD/YYYY");
}

interface ParentRowValues {
    nominator: string;
    email: string;
    guardian_first_name: string;
    guardian_phone_number: string;
    nominated_organization: string;
    survey_nominating_organization: string;
    organization: string;
    birthday: string;
    age: string;
    location: string;
    status: string;
    granted: string;
    projected_amount: number | null;
    granted_amount: number | null;
    disbursed: string;
    reconciled: string;
    created_at: string;
    granted_date: string;
    notes: string;
}

function buildParentRowValues(request: BirthdayFundRequest): ParentRowValues {
    const r = request as ExtendedBirthdayFundRequest;
    return {
        nominator: `${r.survey.data.nominator_first_name || ""} ${r.survey.data.nominator_last_name || ""}`.trim(),
        email: r.survey.data.email || "",
        guardian_first_name: r.survey.data.guardian_first_name || "",
        guardian_phone_number: formatPhone(r.survey.data.guardian_phone_number),
        nominated_organization: r.nominated_organization || "",
        survey_nominating_organization: r.survey.data.nominating_organization || "",
        organization: r.organization || "",
        birthday: formatBirthday(r),
        age: r.survey.data.child_age != null ? String(r.survey.data.child_age) : "N/A",
        location: r.survey.data.party_location || "",
        status: getReadableStatusLabel(r.status),
        granted: r.has_been_granted ? "Yes" : "No",
        projected_amount: parseAmount(r.amount_projected),
        granted_amount: parseAmount(r.amount_granted),
        disbursed: r.has_been_dispersed ? "Yes" : "No",
        reconciled: r.has_been_reconciled ? "Yes" : "No",
        created_at: formatCreatedAt(r.created_at),
        granted_date: formatGrantedDate(r.granted_date),
        notes: r.notes || "",
    };
}

// ---------------------------------------------------------------------------
// Source CSV — flat one-row-per-request snapshot of the requests this report
// was generated from. Uploaded as the tracked-report `source` file.
// ---------------------------------------------------------------------------

async function buildSourceCsv(requests: BirthdayFundRequest[]): Promise<string> {
    // The CSV is the audit input — flat parent rows only, no breakdown rows.
    // It includes every column EXCEPT the per-breakdown "Granted Breakdown".
    const csvKeys: ColumnKey[] = COLUMN_ORDER.filter(
        (k: ColumnKey) => k !== "granted_breakdown",
    );

    const rows: Record<string, string>[] = requests.map((req: BirthdayFundRequest) => {
        const v = buildParentRowValues(req);
        const flat: Record<string, string> = {};
        for (const key of csvKeys) {
            const header = COLUMN_HEADERS[key];
            if (key === "projected_amount" || key === "granted_amount") {
                const num = v[key];
                flat[header] = num == null ? "" : num.toFixed(2);
            } else {
                flat[header] = String(v[key as keyof ParentRowValues] ?? "");
            }
        }
        return flat;
    });

    if (rows.length === 0) {
        // json2csv throws on empty input — emit just a header line.
        return csvKeys.map((k: ColumnKey) => COLUMN_HEADERS[k]).join(",") + "\n";
    }

    return await json2csv(rows, {
        keys: csvKeys.map((k: ColumnKey) => COLUMN_HEADERS[k]),
        emptyFieldValue: "",
        expandNestedObjects: false,
        useDateIso8601Format: true,
    });
}

// ---------------------------------------------------------------------------
// XLSX builder
// ---------------------------------------------------------------------------

const GRANTED_AMOUNT_INDEX = COLUMN_ORDER.indexOf("granted_amount");
const GRANTED_BREAKDOWN_INDEX = COLUMN_ORDER.indexOf("granted_breakdown");

function emptyRow(): CellValue[] {
    return COLUMN_ORDER.map((): CellValue => ({ value: "" }));
}

function buildParentCellRow(values: ParentRowValues): CellValue[] {
    const row: CellValue[] = [];
    for (const key of COLUMN_ORDER) {
        if (key === "granted_breakdown") {
            // Parent row leaves Granted Breakdown blank — only sub rows
            // populate it. This is what allows SUM on that column to only
            // pick up sub-row breakdown amounts.
            row.push({ value: "" });
            continue;
        }
        if (key === "projected_amount" || key === "granted_amount") {
            const num = values[key];
            row.push(
                num == null
                    ? { value: "" }
                    : { value: num, format: CellFormat.ACCOUNTING },
            );
            continue;
        }
        row.push({ value: values[key as keyof ParentRowValues] ?? "" });
    }
    return row;
}

function buildBreakdownSubRow(breakdown: BirthdayFundBreakdown): CellValue[] {
    // Sub rows are blank except for:
    //  - Granted Amount column: breakdown name (string — SUM ignores)
    //  - Granted Breakdown column: breakdown amount (numeric, accounting)
    const row = emptyRow();
    row[GRANTED_AMOUNT_INDEX] = { value: breakdown.name };
    const amt = parseAmount(breakdown.amount);
    row[GRANTED_BREAKDOWN_INDEX] =
        amt == null
            ? { value: "" }
            : { value: amt, format: CellFormat.ACCOUNTING };
    return row;
}

async function buildWorkbookBuffer(requests: BirthdayFundRequest[]): Promise<Buffer> {
    const composer = new ExcelComposer();
    composer.createSheet("Granted Requests");

    const data: CellValue[][] = [];

    for (const request of requests) {
        const parentValues = buildParentRowValues(request);
        data.push(buildParentCellRow(parentValues));

        const breakdowns = normalizeBreakdowns(request)
            .filter((b: BirthdayFundBreakdown) => b.status === "granted")
            .sort((a: BirthdayFundBreakdown, b: BirthdayFundBreakdown) =>
                (a.name || "").localeCompare(b.name || ""),
            );

        for (const breakdown of breakdowns) {
            data.push(buildBreakdownSubRow(breakdown));
        }
    }

    // Footer: "Totals" label in column 0; SUM on Granted Amount and Granted
    // Breakdown columns. Parent rows leave Granted Breakdown blank and sub
    // rows write a string into Granted Amount, so SUM on each column captures
    // only the appropriate values without double-counting.
    const footerCalculations = COLUMN_ORDER.map((key: ColumnKey, idx: number) => {
        if (idx === 0) {
            return { type: "value" as const, value: "Totals", column: idx, bold: true };
        }
        if (key === "granted_amount" || key === "granted_breakdown") {
            return {
                type: "sum" as const,
                column: idx,
                bold: true,
                format: CellFormat.ACCOUNTING,
            };
        }
        return { type: "value" as const, value: "", column: idx };
    });

    // Column widths — give the wider text columns reasonable defaults.
    const columnStyling: Record<string, { width: number; format?: CellFormat }> = {};
    const widthByKey: Partial<Record<ColumnKey, number>> = {
        nominator: 24,
        email: 28,
        guardian_first_name: 18,
        guardian_phone_number: 18,
        nominated_organization: 26,
        survey_nominating_organization: 28,
        organization: 22,
        birthday: 12,
        age: 8,
        location: 22,
        status: 18,
        granted: 10,
        projected_amount: 16,
        granted_amount: 16,
        granted_breakdown: 18,
        disbursed: 12,
        reconciled: 12,
        created_at: 20,
        granted_date: 14,
        notes: 30,
    };
    COLUMN_ORDER.forEach((key: ColumnKey, idx: number) => {
        const colLetter = numberToExcelColumn(idx + 1);
        columnStyling[colLetter] = { width: widthByKey[key] ?? 14 };
    });

    const section: SectionConfig = {
        headers: COLUMN_ORDER.map((k: ColumnKey) => COLUMN_HEADERS[k]),
        data,
        footerCalculations,
        styling: {
            header: { bold: true, border: { type: "bottom" } },
            footer: { border: { type: "top" } },
            columns: columnStyling,
        },
    };

    composer.addSection(section, { startRow: 1, startColumn: 1 });

    return await composer.getBuffer();
}

// ---------------------------------------------------------------------------
// Tracked report API
// ---------------------------------------------------------------------------

async function createTrackedReport(input: {
    source_filename: string;
    output_filename: string;
}): Promise<CreatedTrackedReport> {
    const body = {
        report_type: REPORT_TYPE,
        files: {
            source: [
                {
                    filename: input.source_filename,
                    file_type: MimeType.Csv,
                },
            ],
            output: [
                {
                    filename: input.output_filename,
                    file_type: MimeType.Xlsx,
                },
            ],
        },
    };

    const res = await fetchApiPost<CreatedTrackedReport>("tracked/reports", body);
    if (!res.success || !res.data) {
        throw new Error(
            `Failed to create tracked report: ${res.message || "unknown error"}`,
        );
    }
    return res.data;
}

// ---------------------------------------------------------------------------
// Public button
// ---------------------------------------------------------------------------

export function BirthdayFundGrantedExportButton({
    requests,
}: {
    requests: BirthdayFundRequest[];
}) {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const onClick = async () => {
        if (isSubmitting) return;

        // Defense-in-depth: even if the caller only renders this button when
        // the granted filter is active, re-filter here.
        const grantedRequests = requests
            .filter((r: BirthdayFundRequest) => r.has_been_granted)
            .sort((a: BirthdayFundRequest, b: BirthdayFundRequest) =>
                // desc by created_at to match the table's default sort
                (b.created_at || "").localeCompare(a.created_at || ""),
            );

        if (grantedRequests.length === 0) {
            toast({
                title: "No granted requests to export",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const stem = buildFilenameStem();
            const sourceFilename = `${stem}_source.csv`;
            const outputFilename = `${stem}.xlsx`;

            // 1) Build source CSV (audit input).
            const csvString = await buildSourceCsv(grantedRequests);

            // 2) Create tracked report (presigned PUT URLs for source + output).
            const created = await createTrackedReport({
                source_filename: sourceFilename,
                output_filename: outputFilename,
            });
            const sourcePresign = created.source[0];
            const outputPresign = created.output[0];
            if (!sourcePresign || !outputPresign) {
                throw new Error("Tracked report response is missing presigned URLs.");
            }

            // 3) Upload source CSV — abort if this fails.
            const sourceBlob = new Blob([csvString], {
                type: `${MimeType.Csv};charset=utf-8;`,
            });
            const sourceUpload = await UploadPresignedFile(
                sourcePresign.put_url,
                sourceBlob,
                MimeType.Csv,
            );
            if (!sourceUpload.success) {
                throw new Error(
                    "Failed to upload source CSV to S3. Report not generated.",
                );
            }

            // 4) Build the report.
            const buffer = await buildWorkbookBuffer(grantedRequests);

            // 5) Upload output. If this fails: NO download.
            const outputBlob = new Blob([buffer], { type: MimeType.Xlsx });
            const outputUpload = await UploadPresignedFile(
                outputPresign.put_url,
                outputBlob,
                MimeType.Xlsx,
            );
            if (!outputUpload.success) {
                throw new Error(
                    "Failed to upload generated report to S3. The output was not persisted.",
                );
            }

            // 6) Auto-download.
            downloadBinaryData(buffer, outputFilename, MimeType.Xlsx);

            toast({ title: "Granted export generated" });
        } catch (error: unknown) {
            const msg =
                error instanceof Error
                    ? error.message
                    : "Failed to generate granted export.";
            console.error("Birthday Fund Granted Export failed", error);
            toast({ title: msg, variant: "destructive" });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Button variant="outline" size="sm" onClick={onClick} disabled={isSubmitting}>
            {isSubmitting ? <Loader /> : "Granted Export"}
        </Button>
    );
}
